import { compose, has, prop } from 'ramda';
import { isObject } from 'ramda-extension';
import { redirect, NOT_FOUND, pathToAction } from 'redux-first-router';
import qs from 'qs';
import { createAction } from 'redux-actions';

import { gameDirectoryUrl, checkoutUrl } from '../../lib/url-builders';
import { isServer, isWaybetterProduct } from '../../lib/utils';
import { setJsonItem } from '../../lib/session-storage/json';
import {
  LOGIN_RETURN_PATH_KEY,
  ONBOARDING_WIZARD_ORDER_PARAMS_KEY,
} from '../../lib/session-storage-keys';

import { selectLocation, selectParams } from './selectors';

export const ROUTE_CHECKOUT = 'ROUTE_CHECKOUT';
export const ROUTE_CREATE_GAME_BET_ORDER = 'ROUTE_CREATE_GAME_BET_ORDER';
export const ROUTE_CREATE_GAME_ORDER = 'ROUTE_CREATE_GAME_ORDER';
export const ROUTE_CREATE_MEMBERSHIP_ORDER = 'ROUTE_CREATE_MEMBERSHIP_ORDER';
export const ROUTE_CREATE_ORDER = 'ROUTE_CREATE_ORDER';
export const ROUTE_CONFIRM_PAYOUT_EMAIL = 'ROUTE_CONFIRM_PAYOUT_EMAIL';
export const ROUTE_DELETE_ACCOUNT = 'ROUTE_DELETE_ACCOUNT';
export const ROUTE_FORGOT_PASSWORD = 'ROUTE_FORGOT_PASSWORD';
export const ROUTE_FULL_SIGNUP = 'ROUTE_FULL_SIGNUP';
export const ROUTE_GAME_LANDING_REDIRECT = 'ROUTE_GAME_LANDING_REDIRECT';
export const ROUTE_GAME_REDIRECT = 'ROUTE_GAME_REDIRECT';
export const ROUTE_ACCOUNTABILITY_PARTNER_INVITE_LANDING =
  'ROUTE_ACCOUNTABILITY_PARTNER_INVITE_LANDING';
export const ROUTE_INVITE_LANDING = 'ROUTE_INVITE_LANDING';
export const ROUTE_LOGIN = 'ROUTE_LOGIN';
export const ROUTE_LOGIN_EMAIL = 'ROUTE_LOGIN_EMAIL';
export const ROUTE_LOGOUT = 'ROUTE_LOGOUT';
export const ROUTE_MEMBERSHIP_SIGNUP = 'ROUTE_MEMBERSHIP_SIGNUP';
export const ROUTE_ONBOARDING_QUIZ = 'ROUTE_ONBOARDING_QUIZ';
export const ROUTE_OPTIONAL_BET = 'ROUTE_OPTIONAL_BET';
export const ROUTE_PRODUCT_FORGOT_PASSWORD = 'ROUTE_PRODUCT_FORGOT_PASSWORD';
export const ROUTE_PRODUCT_LOGIN = 'ROUTE_PRODUCT_LOGIN';
export const ROUTE_PRODUCT_LOGIN_EMAIL = 'ROUTE_LOGIN_EMAIL';
export const ROUTE_PRODUCT_LOGOUT = 'ROUTE_PRODUCT_LOGOUT';
export const ROUTE_PRODUCT_RESET_PASSWORD = 'ROUTE_PRODUCT_RESET_PASSWORD';
export const ROUTE_PRODUCT_SIGNUP = 'ROUTE_PRODUCT_SIGNUP';
export const ROUTE_PUSH_TO = 'ROUTE_PUSH_TO';
export const ROUTE_REDIRECT_TO = 'ROUTE_REDIRECT_TO';
export const ROUTE_REFERRAL_SIGNUP = 'ROUTE_REFERRAL_SIGNUP';
export const ROUTE_RESET_PASSWORD = 'ROUTE_RESET_PASSWORD';
export const ROUTE_ROUTINES_CAMPAIGN = 'ROUTE_ROUTINES_CAMPAIGN';
export const ROUTE_SIGNUP = 'ROUTE_SIGNUP';
export const SAVE_RETURN_PATH = 'SAVE_RETURN_PATH';
export const TO_POST_LOGIN = 'TO_POST_LOGIN';

const isAction = (maybeAnAction) =>
  [isObject, has('type')].every((check) => check(maybeAnAction));

export const redirectTo = createAction(
  ROUTE_REDIRECT_TO,
  (actionOrPath) => (dispatch, getState) => {
    const { routesMap } = compose(selectLocation, getState)();
    const action = isAction(actionOrPath)
      ? actionOrPath
      : pathToAction(actionOrPath, routesMap, qs);

    compose(dispatch, redirect)(action);
  }
);
export const toNotFound = (payload) => redirect({ type: NOT_FOUND, payload });

export const sendTo = createAction(
  ROUTE_PUSH_TO,
  (actionOrPath) => (dispatch, getState) => {
    const { routesMap } = compose(selectLocation, getState)();
    const action = isAction(actionOrPath)
      ? actionOrPath
      : pathToAction(actionOrPath, routesMap, qs);

    dispatch(action);
  }
);

export const toCreateGameOrder = createAction(
  ROUTE_CREATE_GAME_ORDER,
  ({ product = 'waybetter', ...payload }) => ({
    ...payload,
    product: isWaybetterProduct(product) ? undefined : product,
  })
);
export const toCreateGameBetOrder = createAction(ROUTE_CREATE_GAME_BET_ORDER);
export const toCreateMembershipOrder = createAction(
  ROUTE_CREATE_MEMBERSHIP_ORDER
);
export const toCreateOrder = createAction(ROUTE_CREATE_ORDER);

export const toOnboardingQuiz = createAction(
  ROUTE_ONBOARDING_QUIZ,
  (payload) => {
    if (!isServer && payload.orderParams) {
      setJsonItem(ONBOARDING_WIZARD_ORDER_PARAMS_KEY, payload.orderParams);
      return {};
    }
    return payload; // can include "step" url param
  }
);

// Authentication
export const toEmailLogin = createAction(ROUTE_LOGIN);
export const toSignup = createAction(ROUTE_SIGNUP);
export const toMembershipSignup = createAction(ROUTE_MEMBERSHIP_SIGNUP);
export const toFullSignup = createAction(ROUTE_FULL_SIGNUP);
export const toForgotPassword = createAction(ROUTE_FORGOT_PASSWORD);
export const toResetPassword = createAction(ROUTE_RESET_PASSWORD);
export const toOptionalBet = createAction(ROUTE_OPTIONAL_BET);

export const toCheckout = createAction(
  ROUTE_CHECKOUT,
  ({ product, orderId, cartVariant, orderItemInputs, version }) => {
    const path = checkoutUrl({
      product,
      orderId,
      cartVariant,
      orderItemInputs,
      version,
    });
    return (dispatch) => {
      compose(dispatch, redirectTo)(path);
    };
  }
);

export const toPostLogin = createAction(TO_POST_LOGIN, (product) => {
  let path = gameDirectoryUrl({ product });
  if (!isServer) {
    path = window.sessionStorage.getItem(LOGIN_RETURN_PATH_KEY) || path;
    window.sessionStorage.removeItem(LOGIN_RETURN_PATH_KEY);
  }

  return (dispatch, getState) => {
    const { routesMap } = compose(selectLocation, getState)();
    const action = pathToAction(path, routesMap, qs);

    compose(dispatch, redirect)(action);
  };
});

export const saveReturnPath = createAction(
  SAVE_RETURN_PATH,
  () => (dispatch, getState) => {
    if (isServer) return;

    const returnPath = compose(prop('returnPath'), selectParams, getState)();

    returnPath &&
      window.sessionStorage.setItem(LOGIN_RETURN_PATH_KEY, returnPath);
  }
);
